import React, { useState } from 'react';

import { Box, Button, Input, Slider, Typography } from '@mui/joy';

import type { FlightHUDWidgetProps } from '../pilot/components/FlightHUDWidget';
import { PilotTelemetryStore, pilotTelemetryStore } from '../stores/PilotTelemetryStore';
import { TelemetryState, defaultTelemetryState, toHudProps } from './useSimulatorTelemetry';

interface FlightControlSimulatorProps {
  telemetry?: TelemetryState;
  onTelemetryChange?: (t: TelemetryState) => void;
  store?: PilotTelemetryStore;
}

const FlightControlSimulator: React.FC<FlightControlSimulatorProps> = ({
  telemetry: externalTelemetry,
  onTelemetryChange,
  store = pilotTelemetryStore
}) => {
  const [internalTelemetry, setInternalTelemetry] = useState<TelemetryState>(defaultTelemetryState);

  const telemetry = store ? store.telemetry : externalTelemetry ?? internalTelemetry;

  const handleChange = <K extends keyof TelemetryState>(key: K, value: TelemetryState[K]) => {
    const updated = { ...telemetry, [key]: value };
    if (store) {
      store.updateTelemetry({ [key]: value } as Partial<TelemetryState>);
    } else if (externalTelemetry && onTelemetryChange) {
      onTelemetryChange(updated);
    } else {
      setInternalTelemetry(updated);
    }
  };

  const reset = () => {
    if (store) {
      store.reset();
    } else if (externalTelemetry && onTelemetryChange) {
      onTelemetryChange(defaultTelemetryState);
    } else {
      setInternalTelemetry(defaultTelemetryState);
    }
  };

  const [startQuery, setStartQuery] = useState('');
  const hudProps: FlightHUDWidgetProps = toHudProps(telemetry);

  return (
    <Box className='flex flex-col md:flex-row w-full h-full p-4 space-y-4 md:space-y-0 md:space-x-4 z-10'>
      <Box className='md:w-1/3 order-2 md:order-1 overflow-y-auto space-y-4' sx={{ maxHeight: '80vh' }}>
        <Typography level='h4'>Controls</Typography>
        <Box>
          <Typography level='body-sm'>Gimbal Pitch ({telemetry.gimbalPitch}°)</Typography>
          <Slider
            min={-90}
            max={30}
            value={telemetry.gimbalPitch}
            onChange={(_, v) => handleChange('gimbalPitch', Array.isArray(v) ? v[0] : v)}
          />
        </Box>
        <Box>
          <Typography level='body-sm'>Gimbal Yaw ({telemetry.gimbalYaw}°)</Typography>
          <Slider
            min={0}
            max={360}
            value={telemetry.gimbalYaw}
            onChange={(_, v) => handleChange('gimbalYaw', Array.isArray(v) ? v[0] : v)}
          />
        </Box>
        <Box>
          <Typography level='body-sm'>Zoom ({telemetry.zoom.toFixed(1)}x)</Typography>
          <Slider
            min={0}
            max={20}
            step={0.1}
            value={telemetry.zoom}
            onChange={(_, v) => handleChange('zoom', Array.isArray(v) ? v[0] : v)}
          />
        </Box>
        <Box>
          <Typography level='body-sm'>Altitude ({telemetry.altitude} ft)</Typography>
          <Slider
            min={0}
            max={400}
            value={telemetry.altitude}
            onChange={(_, v) => handleChange('altitude', Array.isArray(v) ? v[0] : v)}
          />
        </Box>
        <Box>
          <Typography level='body-sm'>Horizontal Speed ({telemetry.horizontalSpeed} mph)</Typography>
          <Slider
            min={0}
            max={50}
            value={telemetry.horizontalSpeed}
            onChange={(_, v) => handleChange('horizontalSpeed', Array.isArray(v) ? v[0] : v)}
          />
        </Box>
        <Box>
          <Typography level='body-sm'>Vertical Speed ({telemetry.verticalSpeed} mph)</Typography>
          <Slider
            min={-10}
            max={10}
            value={telemetry.verticalSpeed}
            onChange={(_, v) => handleChange('verticalSpeed', Array.isArray(v) ? v[0] : v)}
          />
        </Box>
        <Box>
          <Typography level='body-sm'>Heading ({telemetry.heading}°)</Typography>
          <Slider
            min={0}
            max={360}
            value={telemetry.heading}
            onChange={(_, v) => handleChange('heading', Array.isArray(v) ? v[0] : v)}
          />
        </Box>
        <Box>
          <Typography level='body-sm'>Wind Speed ({telemetry.windSpeed} mph)</Typography>
          <Slider
            min={0}
            max={30}
            value={telemetry.windSpeed}
            onChange={(_, v) => handleChange('windSpeed', Array.isArray(v) ? v[0] : v)}
          />
        </Box>
        <Box>
          <Typography level='body-sm'>Wind Direction ({telemetry.windDirection}°)</Typography>
          <Slider
            min={0}
            max={360}
            value={telemetry.windDirection}
            onChange={(_, v) => handleChange('windDirection', Array.isArray(v) ? v[0] : v)}
          />
        </Box>
        <Box>
          <Typography level='body-sm'>Battery ({telemetry.batteryPercent}%)</Typography>
          <Slider
            min={0}
            max={100}
            value={telemetry.batteryPercent}
            onChange={(_, v) => handleChange('batteryPercent', Array.isArray(v) ? v[0] : v)}
          />
        </Box>
        <Box>
          <Typography level='body-sm'>ETA to Home</Typography>
          <Input value={telemetry.estTimeToHome} onChange={(e) => handleChange('estTimeToHome', e.target.value)} />
        </Box>
        <Box>
          <Typography level='body-sm'>Time Until 10% Batt</Typography>
          <Input
            value={telemetry.estTimeUntilLowBattery}
            onChange={(e) => handleChange('estTimeUntilLowBattery', e.target.value)}
          />
        </Box>
        {store && (
          <Box className='space-y-1'>
            <Typography level='body-sm'>Start Location</Typography>
            <Input value={startQuery} onChange={(e) => setStartQuery(e.target.value)} placeholder='Search address' />
            <Button size='sm' onClick={() => store.setStartLocation(startQuery)}>
              Set Start
            </Button>
          </Box>
        )}
        <Button variant='soft' color='neutral' onClick={reset} fullWidth>
          Reset to Defaults
        </Button>
      </Box>
      <Box className='flex-1 order-1 md:order-2'>
        <Box className='bg-neutral-800 text-white p-2 rounded' sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
          <pre className='whitespace-pre-wrap'>{JSON.stringify(hudProps, null, 2)}</pre>
        </Box>
      </Box>
    </Box>
  );
};

export default FlightControlSimulator;
